import React, { useState, useEffect, useCallback } from 'react';
import { FiGrid, FiChevronDown, FiX } from 'react-icons/fi';

// ── CSS injected once ──────────────────────────────────────────────────────
const STYLE_ID = 'power-dash-menu-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .pdm-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 10px; border-radius: 6px; cursor: pointer;
      font-size: 12px; font-weight: 500; color: #4A5568;
      background: transparent; border: none; outline: none;
      transition: background 0.15s;
      font-family: inherit;
      user-select: none;
    }
    .pdm-btn:hover, .pdm-btn.open { background: rgba(0,0,0,0.06); }
    .pdm-btn .pdm-chevron { transition: transform 0.22s ease; }
    .pdm-btn.open .pdm-chevron { transform: rotate(180deg); }

    /* Dark semi-transparent backdrop */
    .pdm-backdrop {
      position: fixed; inset: 0; z-index: 9000;
      background: rgba(13,27,62,0.45);
      backdrop-filter: blur(3px);
      opacity: 0;
      transition: opacity 0.2s ease;
    }
    .pdm-backdrop.visible { opacity: 1; }

    /* Centred modal card */
    .pdm-modal {
      position: fixed;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%) scale(0.94);
      z-index: 9001;
      background: #fff;
      border-radius: 14px;
      box-shadow: 0 24px 64px rgba(0,0,0,0.22), 0 8px 20px rgba(0,0,0,0.12);
      overflow: hidden;
      opacity: 0;
      pointer-events: none;
      transition:
        opacity 0.2s cubic-bezier(0.4,0,0.2,1),
        transform 0.2s cubic-bezier(0.4,0,0.2,1);
      max-width: calc(100vw - 48px);
      max-height: calc(100vh - 80px);
    }
    .pdm-modal.visible {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
      pointer-events: auto;
    }

    /* Modal header bar */
    .pdm-modal-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 16px 10px;
      border-bottom: 1px solid #E2E8F0;
    }
    .pdm-modal-title {
      display: flex; align-items: center; gap: 7px;
      font-size: 13px; font-weight: 700; color: #1A202C;
    }
    .pdm-close-btn {
      display: flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border-radius: 6px;
      border: none; background: transparent; cursor: pointer;
      color: #718096; transition: background 0.13s, color 0.13s;
    }
    .pdm-close-btn:hover { background: #F1F5F9; color: #1A202C; }

    /* Sections row — always side by side */
    .pdm-sections {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 10px;
      padding: 14px;
      overflow-x: auto;
    }

    .pdm-section {
      border: 1.5px solid #EE7203;
      border-radius: 8px;
      overflow: hidden;
      min-width: 175px;
      flex-shrink: 0;
    }

    .pdm-section-header {
      background: #EE7203;
      padding: 5px 10px;
    }
    .pdm-section-header span {
      font-size: 9px; font-weight: 800;
      text-transform: uppercase; letter-spacing: 0.09em; color: #fff;
    }

    .pdm-item { display: flex; align-items: center; gap: 9px; padding: 8px 13px;
      font-size: 12.5px; color: #2D3748; cursor: pointer; transition: background 0.11s;
      white-space: nowrap; background: transparent; border: none; width: 100%; text-align: left;
      font-family: inherit; }
    .pdm-item:hover { background: #FFF5ED; color: #C05A00; }
    .pdm-item:hover svg { color: #EE7203; }
    .pdm-item svg { color: #A0AEC0; flex-shrink: 0; transition: color 0.11s; }
    .pdm-item.pdm-disabled { opacity: 0.4; cursor: not-allowed; pointer-events: none; }
    .pdm-item.pdm-disabled:hover { background: transparent; color: #2D3748; }
  `;
  document.head.appendChild(style);
}

// ── Component ──────────────────────────────────────────────────────────────
export default function PowerDashMenu({ sections }) {
  const [open, setOpen]       = useState(false);
  const [visible, setVisible] = useState(false);

  const close = useCallback(() => {
    setVisible(false);
    setTimeout(() => setOpen(false), 210);
  }, []);

  const openMenu = useCallback(() => {
    if (open) { close(); return; }
    setOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
  }, [open, close]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <>
      <button
        className={`pdm-btn${open ? ' open' : ''}`}
        onClick={openMenu}
        aria-haspopup="dialog"
        aria-expanded={open}>
        <FiGrid size={14} />
        <span>Power Dashboards</span>
        <FiChevronDown size={13} className="pdm-chevron" />
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className={`pdm-backdrop${visible ? ' visible' : ''}`}
          onClick={close}
        />
      )}

      {/* Centred modal */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Power Dashboards"
          className={`pdm-modal${visible ? ' visible' : ''}`}
        >
          <div className="pdm-modal-header">
            <div className="pdm-modal-title">
              <FiGrid size={15} color="#EE7203" />
              Power Dashboards
            </div>
            <button className="pdm-close-btn" onClick={close} aria-label="Close">
              <FiX size={15} />
            </button>
          </div>

          <div className="pdm-sections">
            {sections.map(section => (
              <div key={section.label} className="pdm-section">
                <div className="pdm-section-header">
                  <span>{section.label}</span>
                </div>
                {section.items.map((item, i) => (
                  <button
                    key={i}
                    className={`pdm-item${item.disabled ? ' pdm-disabled' : ''}`}
                    title={item.disabledReason || undefined}
                    onClick={() => { if (item.disabled) return; close(); item.onClick(); }}>
                    {item.icon && <item.icon size={14} />}
                    {item.label}
                    {item.disabled && <span style={{ fontSize:'9px', marginLeft:'auto', color:'#A0AEC0' }}>🔒</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
